import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Parallax } from 'react-parallax';
import { 
  ShieldCheckIcon, 
  ChevronDownIcon,
  CubeTransparentIcon,
  ClockIcon,
  DocumentCheckIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ChartBarIcon,
  HandRaisedIcon,
  FireIcon,
  LightBulbIcon,
  StarIcon,
  TrophyIcon,
  BanknotesIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import AnimatedHero from '../components/AnimatedHero';
import ComparisonSlider from '../components/ComparisonSlider';
import LiveProtectionCounter from '../components/LiveProtectionCounter';
import AnimatedBackground from '../components/AnimatedBackground';
import AnimatedSection from '../components/AnimatedSection';
import AnimatedText from '../components/AnimatedText';
import AnimatedCard from '../components/AnimatedCard';
import FloatingNav from '../components/FloatingNav';
import ScrollIndicator from '../components/ScrollIndicator';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import HorizontalScrollSection from '../components/HorizontalScrollSection';
import ScrollStorySection from '../components/ScrollStorySection';
import ParallaxStorySection from '../components/ParallaxStorySection';

gsap.registerPlugin(ScrollTrigger);

const Homepage = React.memo(() => {
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.08], [1, 0]);
  const [openFaq, setOpenFaq] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const contentRef = useRef(null);

  const toggleFaq = useCallback((index) => {
    setOpenFaq(openFaq === index ? null : index);
  }, [openFaq]);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Memoize static data to prevent recreation
  const storyData = useMemo(() => ({
    scrollStories: [
      {
        title: "The Risk",
        description: "Every year, thousands of businesses face unexpected IRS audits.",
        icon: ExclamationTriangleIcon,
        content: (
          <div className="mt-8 grid grid-cols-2 gap-6">
            <div className="text-4xl font-bold text-red-600">1 in 7</div>
            <div className="text-gray-600">Businesses audited within 5 years</div>
          </div>
        )
      },
      {
        title: "The Cost",
        description: "Without protection, an audit can be financially devastating.",
        icon: CurrencyDollarIcon,
        content: (
          <div className="mt-8 grid grid-cols-2 gap-6">
            <div className="text-4xl font-bold text-orange-600">$20K+</div>
            <div className="text-gray-600">Average audit defense cost</div>
          </div>
        )
      },
      {
        title: "The Solution",
        description: "Comprehensive protection that keeps you safe.",
        icon: ShieldCheckIcon,
        content: (
          <div className="mt-8 grid grid-cols-2 gap-6">
            <div className="text-4xl font-bold text-green-600">100%</div>
            <div className="text-gray-600">Coverage of audit costs</div>
          </div>
        )
      }
    ],
    parallaxStories: [
      {
        title: "Protect Your Business",
        description: "In today's complex tax environment, IRS audits can happen to anyone. Be prepared with comprehensive protection.",
        image: "/images/business-protection.jpg",
        content: (
          <div className="grid grid-cols-2 gap-6 mt-8">
            <div className="bg-blue-50 rounded-xl p-6">
              <div className="text-3xl font-bold text-blue-600">24/7</div>
              <div className="text-blue-800">Protection</div>
            </div>
            <div className="bg-green-50 rounded-xl p-6">
              <div className="text-3xl font-bold text-green-600">$250K</div>
              <div className="text-green-800">Coverage</div>
            </div>
          </div>
        )
      },
      {
        title: "Expert Defense Team",
        description: "Get access to top tax attorneys and CPAs who specialize in IRS audit defense.",
        image: "/images/expert-team.jpg",
        content: (
          <div className="bg-purple-50 rounded-xl p-6 mt-8">
            <div className="text-3xl font-bold text-purple-600">20+ Years</div>
            <div className="text-purple-800">Average Experience</div>
          </div>
        )
      },
      {
        title: "Peace of Mind",
        description: "Focus on growing your business while we handle the complexities of IRS compliance.",
        image: "/images/peace-of-mind.jpg",
        content: (
          <div className="grid grid-cols-2 gap-6 mt-8">
            <div className="bg-orange-50 rounded-xl p-6">
              <div className="text-3xl font-bold text-orange-600">98%</div>
              <div className="text-orange-800">Success Rate</div>
            </div>
            <div className="bg-teal-50 rounded-xl p-6">
              <div className="text-3xl font-bold text-teal-600">15K+</div>
              <div className="text-teal-800">Cases Won</div>
            </div>
          </div>
        )
      }
    ],
    socialProofStats: [
      { number: "98%", label: "Client Retention Rate" },
      { number: "$50M+", label: "Penalties Prevented" },
      { number: "15,000+", label: "Audits Defended" }
    ],
    riskPoints: [
      { 
        title: "Client Relationship Risk",
        description: "When clients face unexpected penalties, they often blame their accountant first. Don't let IRS audits damage your hard-earned relationships.",
        icon: HandRaisedIcon,
        color: "red"
      },
      { 
        title: "Revenue Loss",
        description: "Unhappy clients mean lost revenue. The average accounting firm loses 23% of clients after an adverse audit outcome.",
        icon: BanknotesIcon,
        color: "orange"
      },
      { 
        title: "Reputation Damage",
        description: "In today's connected world, one negative review about audit handling can impact years of reputation building.",
        icon: StarIcon,
        color: "yellow"
      },
      { 
        title: "Competitive Disadvantage",
        description: "Leading firms are already offering audit protection. Don't let competitors win clients by providing better security.",
        icon: ChartBarIcon,
        color: "blue"
      }
    ],
    faqData: [
      {
        question: "If I file everything correctly, will I still get audited?",
        answer: "Yes, even if your tax return is 100% accurate and compliant, you can still be audited. The IRS conducts audits for several reasons beyond errors, including random selection, enforcement priorities, and comparative analysis."
      },
      {
        question: "What are the potential costs of an IRS audit?",
        answer: "An IRS audit can cost a small business anywhere from $10,000 to over $100,000, depending on complexity. This includes defense costs ($10,000-$30,000), tax adjustments ($14,000-$18,000 average), penalties, interest, and potential tax court costs ($50,000-$100,000)."
      },
      {
        question: "If there's an error on my tax return, isn't my CPA accountable?",
        answer: "While your CPA is responsible for preparing your return accurately, you as the business owner are legally and financially responsible for the information filed. CPA's E&O insurance has limitations and typically doesn't protect you from the full financial impact of an IRS audit."
      },
      {
        question: "What types of tax audits are covered?",
        answer: "Coverage includes all federal income tax audits conducted by the IRS, including correspondence audits, office audits, and field audits. State tax audits, VAT, sales tax, and payroll tax audits are not covered."
      }
    ]
  }), []);

  // Optimized initialization with reduced complexity
  useEffect(() => {
    gsap.set(contentRef.current, { opacity: 0 });

    const showContent = () => {
      gsap.to(contentRef.current, {
        opacity: 1,
        duration: 0.8, // Reduced duration
        ease: 'power2.inOut'
      });
    };

    // Use requestIdleCallback for non-critical operations
    if (window.requestIdleCallback) {
      window.requestIdleCallback(showContent);
    } else {
      setTimeout(showContent, 100);
    }

    return () => {
      if (contentRef.current) {
        gsap.set(contentRef.current, { clearProps: 'all' });
      }
      // Clean up only necessary ScrollTriggers
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger && trigger.trigger.closest('.homepage-content')) {
          trigger.kill();
        }
      });
    };
  }, []);

  return (
    <div ref={contentRef} className="min-h-screen bg-white">
      <FloatingNav />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="absolute inset-0 opacity-40">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f1f5f9' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="text-left">
            
            {/* Trust Badge */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-2 mb-8"
            >
              <ShieldCheckIcon className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                Trusted by 2,500+ Businesses
              </span>
            </motion.div>

              {/* Main Headline */}
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-8 leading-tight"
              >
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
                  IRS Audit Protection
                </div>
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                  That Actually Works
                </div>
              </motion.h1>

            {/* Subheadline */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-gray-600 mb-12 leading-relaxed"
            >
              Monthly subscription gives you instant access to our collection of experienced tax attorneys. 
              When the IRS comes knocking, we handle everything—no surprises, no hidden costs.
            </motion.p>

              {/* CTA Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/intake"
                  className="group bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Get Protected Now
                  <ArrowRightIcon className="inline-block h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="https://calendly.com/irsaudit"
                  target="_blank"
                  rel="noreferrer"
                  className="group border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all duration-300"
                >
                  Free Consultation
                  <ArrowRightIcon className="inline-block h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </motion.div>
            </div>

            {/* Right Column - Visual/Stats */}
            <div className="relative space-y-6">
              {/* Social Proof */}
             

              {/* Live Stats */}
              <motion.div 
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Live Protection Stats</h3>
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                    <div>
                      <p className="text-sm text-gray-600">Active Subscribers</p>
                      <p className="text-2xl font-bold text-blue-600">2,847</p>
                    </div>
                    <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                    <div>
                      <p className="text-sm text-gray-600">Audits Defended</p>
                      <p className="text-2xl font-bold text-green-600">1,234</p>
                    </div>
                    <ScaleIcon className="h-8 w-8 text-green-600" />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
                    <div>
                      <p className="text-sm text-gray-600">Success Rate</p>
                      <p className="text-2xl font-bold text-purple-600">94.7%</p>
                    </div>
                    <TrophyIcon className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
              </motion.div>
              <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-6"
            >
              <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <ShieldCheckIcon className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm">Monthly Protection</h3>
                <p className="text-xs text-gray-600">Fixed monthly fee</p>
              </div>
              
              <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <UserGroupIcon className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm">Expert Attorneys</h3>
                <p className="text-xs text-gray-600">20+ years experience</p>
              </div>
              
              <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <CurrencyDollarIcon className="h-5 w-5 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm">Full Coverage</h3>
                <p className="text-xs text-gray-600">All costs included</p>
              </div>
            </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="inline-flex items-center space-x-2 bg-red-50 border border-red-200 rounded-full px-4 py-2 mb-8"
            >
              <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-800">The Reality</span>
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              IRS Audits Are More Common Than You Think
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Every year, thousands of businesses face unexpected IRS audits. Without proper protection, 
              the financial and emotional toll can be devastating.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                stat: "1 in 6",
                label: "Small businesses face an IRS audit",
                description: "The IRS audits small businesses at a higher rate than individuals",
                color: "red"
              },
              {
                stat: "$20K+",
                label: "Average audit defense cost",
                description: "Professional representation costs can quickly add up",
                color: "orange"
              },
              {
                stat: "9-12",
                label: "Months average audit duration",
                description: "Audits can drag on for nearly a year, disrupting your business",
                color: "yellow"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center p-8 bg-white rounded-2xl shadow-sm border border-gray-100"
              >
                <div className={`w-16 h-16 bg-${item.color}-100 rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <span className={`text-2xl font-bold text-${item.color}-600`}>{item.stat}</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{item.label}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="inline-flex items-center space-x-2 bg-green-50 border border-green-200 rounded-full px-4 py-2 mb-8"
            >
              <ShieldCheckIcon className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">The Solution</span>
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Monthly Protection That Actually Works
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Get instant access to experienced tax attorneys for a fixed monthly fee. 
              When the IRS audits you, we handle everything—no surprises, no hidden costs.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="space-y-8">
                {[
                  {
                    icon: ShieldCheckIcon,
                    title: "Monthly Subscription",
                    description: "Fixed monthly fee gives you instant access to our team of tax attorneys",
                    color: "blue"
                  },
                  {
                    icon: UserGroupIcon,
                    title: "Expert Representation",
                    description: "Experienced attorneys handle all IRS correspondence and audit defense",
                    color: "green"
                  },
                  {
                    icon: CurrencyDollarIcon,
                    title: "No Hidden Costs",
                    description: "All audit costs included in your monthly subscription—no surprises",
                    color: "purple"
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-4"
                  >
                    <div className={`w-12 h-12 bg-${item.color}-100 rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <item.icon className={`h-6 w-6 text-${item.color}-600`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">How It Works</h3>
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                      <span className="text-gray-700">Subscribe to monthly protection plan</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                      <span className="text-gray-700">Get instant access to tax attorneys</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                      <span className="text-gray-700">We handle any IRS audit that comes your way</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="inline-flex items-center space-x-2 bg-green-50 border border-green-200 rounded-full px-4 py-2 mb-8"
            >
              <CurrencyDollarIcon className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Simple Pricing</span>
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              One Plan, Complete Protection
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              No complex pricing tiers. One monthly subscription covers everything you need for IRS audit protection.
            </motion.p>
          </div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8 md:p-12 text-center"
            >
              <div className="mb-8">
                <div className="text-5xl font-bold text-gray-900 mb-2">$99 - $299</div>
                <div className="text-xl text-gray-600">per month</div>
                <div className="text-sm text-gray-500 mt-2">Custom pricing based on your business needs</div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Included:</h3>
                  {[
                    "Access to experienced tax attorneys",
                    "Full IRS audit representation",
                    "All correspondence handled",
                    "Document preparation support",
                    "No additional fees or costs"
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Coverage:</h3>
                  {[
                    "Up to $250,000 per audit",
                    "All federal income tax audits",
                    "Correspondence & field audits",
                    "Tax court representation",
                    "Penalty & interest coverage"
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <Link
                to="/intake"
                className="inline-block bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg"
              >
                Get Protected Now
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="inline-flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-2 mb-8"
            >
              <QuestionMarkCircleIcon className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Frequently Asked Questions</span>
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Common Questions
            </motion.h2>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "What does the monthly subscription include?",
                answer: "Your monthly subscription includes instant access to our team of experienced tax attorneys, full IRS audit representation, handling of all correspondence, document preparation support, and coverage of all audit-related costs up to $250,000 per audit."
              },
              {
                question: "Can I cancel my subscription anytime?",
                answer: "Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees. Your protection continues through the end of your current billing period."
              },
              {
                question: "What types of audits are covered?",
                answer: "We cover all federal income tax audits conducted by the IRS, including correspondence audits, office audits, and field audits. This includes tax court representation if needed."
              },
              {
                question: "How quickly can I get help if I'm audited?",
                answer: "Once you're subscribed, you have instant access to our team. If you receive an audit notice, simply contact us and we'll begin handling everything immediately—typically within 24 hours."
              },
              {
                question: "Is there a deductible or additional costs?",
                answer: "No, there are no deductibles or additional costs. Your monthly subscription covers everything—all attorney fees, document preparation, correspondence, and audit defense costs are included."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDownIcon
                    className={`w-5 h-5 text-gray-500 transform transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="px-8 pb-6"
                  >
                    <p className="text-gray-600">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Protect Your Business?
            </h2>
            <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto">
              Join thousands of businesses who trust us with their IRS audit protection. 
              Get started today and sleep better tonight.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/intake"
                className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                Get Protected Now
              </Link>
              <a
                href="https://calendly.com/irsaudit"
                target="_blank"
                rel="noreferrer"
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-colors"
              >
                Free Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
});

Homepage.displayName = 'Homepage';

export default Homepage;